import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, sessionExpired, reconnect } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--bg-canvas)',
        gap: '16px',
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid var(--accent-subtle)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{
          color: 'var(--fg-muted)',
          fontSize: '14px',
          fontFamily: 'Inter, system-ui, sans-serif',
          letterSpacing: '0.5px',
        }}>
          {t('common.loading')}
        </span>
      </div>
    );
  }

  // Session expired — show overlay on top of current content so user doesn't lose context
  if (sessionExpired) {
    return (
      <>
        {children}
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          gap: '16px',
        }}>
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--accent-border)',
            borderRadius: '12px',
            padding: '32px 40px',
            textAlign: 'center',
            maxWidth: '400px',
            boxShadow: 'var(--shadow-lg)',
          }}>
            <div style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--fg-primary)',
              marginBottom: '8px',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}>
              {t('auth.sessionExpired')}
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--fg-secondary)',
              marginBottom: '24px',
              lineHeight: '1.5',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}>
              {t('auth.sessionExpiredDesc')}
            </div>
            <button
              onClick={reconnect}
              style={{
                background: 'var(--accent)',
                color: 'var(--fg-on-accent)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 28px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif',
                transition: 'background 0.2s',
              }}
              onMouseOver={e => (e.currentTarget.style.background = 'var(--accent-hover)')}
              onMouseOut={e => (e.currentTarget.style.background = 'var(--accent)')}
            >
              {t('auth.reconnect')}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
